/**
 * EIP-712 play permit.
 *
 * These type definitions MUST stay byte-identical to three places at once:
 *   - the typehash strings in contracts/src/ScratchyGame.sol
 *   - contracts/relayer/src/types.ts
 *   - here
 *
 * A single renamed or reordered field and every signature fails on-chain with
 * an opaque BadSigner. contracts/test/ScratchyGameSig.t.sol pins a real viem
 * signature against the contract so that drift breaks CI rather than
 * production — regenerate its fixture with relayer/scripts/sigfixture.mjs if
 * you change anything here.
 */
import { TypedDataEncoder, type Signer } from "ethers";

export const EIP712_DOMAIN_NAME = "ScratchyGame";
export const EIP712_DOMAIN_VERSION = "1";

export const playPermitTypes = {
  PlayParams: [
    { name: "currency", type: "address" },
    { name: "owner", type: "address" },
    { name: "tierId", type: "uint8" },
    { name: "count", type: "uint8" },
    { name: "playerSeed", type: "bytes32" }
  ],
  PermitParams: [
    { name: "delegate", type: "address" },
    { name: "deadline", type: "uint256" }
  ],
  PlayPermit: [
    { name: "params", type: "PlayParams" },
    { name: "permit", type: "PermitParams" }
  ]
};

export interface PlayParams {
  currency: string;
  owner: string;
  tierId: number;
  count: number;
  playerSeed: string;
}

export interface PermitParams {
  delegate: string;
  deadline: bigint;
}

export function domain(chainId: number, verifyingContract: string) {
  return {
    name: EIP712_DOMAIN_NAME,
    version: EIP712_DOMAIN_VERSION,
    chainId,
    verifyingContract
  };
}

/**
 * Sign a play permit with the session key.
 *
 * ethers' signTypedData omits EIP712Domain from `types` automatically, which is
 * what the contract expects. This never touches the player's wallet — that is
 * the entire point of the session key.
 */
export async function signPlayPermit(
  signer: Signer,
  chainId: number,
  gameAddress: string,
  params: PlayParams,
  permit: PermitParams
): Promise<string> {
  return signer.signTypedData(domain(chainId, gameAddress), playPermitTypes, { params, permit });
}

/** Local digest, for debugging a rejected signature without a round trip. */
export function playPermitDigest(
  chainId: number,
  gameAddress: string,
  params: PlayParams,
  permit: PermitParams
): string {
  return TypedDataEncoder.hash(domain(chainId, gameAddress), playPermitTypes, { params, permit });
}
